import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { formatEuro } from "../lib/format";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from "recharts";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Scale, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

const RANGES = [
  { value: "12", label: "12 mo" },
  { value: "24", label: "24 mo" },
  { value: "60", label: "5 yr" },
  { value: "120", label: "All" },
];

export default function NetWorthPage() {
  const [months, setMonths] = useState("24");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/networth/timeline`, { params: { months } });
        setData(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [months]);

  const timeline = data?.timeline || [];

  const stats = useMemo(() => {
    if (!timeline.length) return null;
    const first = timeline[0];
    const last = timeline[timeline.length - 1];
    const change = last.net_worth - first.net_worth;
    const pct = first.net_worth !== 0 ? (change / Math.abs(first.net_worth)) * 100 : null;
    return {
      latest: last,
      change,
      pct,
      months: timeline.length,
    };
  }, [timeline]);

  const formatMonthShort = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
  };

  return (
    <div className="space-y-8 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="label-kicker">Trajectory</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A] mt-2">
            Net worth over time
          </h1>
          <p className="text-[#5C635A] mt-2 max-w-2xl">
            A monthly view of your family balance — assets minus debts, calculated from every
            recorded value update and payment.
          </p>
        </div>
        <Tabs value={months} onValueChange={setMonths}>
          <TabsList className="bg-[#EEEBE2]" data-testid="networth-range-tabs">
            {RANGES.map((r) => (
              <TabsTrigger
                key={r.value}
                value={r.value}
                className="data-[state=active]:bg-white data-[state=active]:text-[#1D3328]"
                data-testid={`networth-range-${r.value}`}
              >
                {r.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {loading && !data ? (
        <div className="text-[#8B9389] tracking-[0.2em] uppercase text-xs">Loading…</div>
      ) : timeline.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <Scale className="w-10 h-10 mx-auto text-[#8B9389]" strokeWidth={1.5} />
          <h3 className="font-display text-2xl text-[#1D3328] mt-4">Not enough history yet</h3>
          <p className="text-[#5C635A] mt-2 max-w-md mx-auto">
            Add a debt, an asset, or record a payment with a back-date to see your timeline take
            shape.
          </p>
        </div>
      ) : (
        <>
          {stats && (
            <section className="card-soft p-6 sm:p-8" data-testid="networth-headline">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                <div>
                  <p className="label-kicker">Latest ({stats.latest.month})</p>
                  <div
                    className="font-display text-4xl sm:text-5xl font-medium num mt-2"
                    style={{ color: stats.latest.net_worth >= 0 ? "#2C4A3B" : "#B04A32" }}
                    data-testid="networth-latest-value"
                  >
                    {formatEuro(stats.latest.net_worth)}
                  </div>
                </div>
                <div>
                  <p className="label-kicker">Change over {stats.months} mo</p>
                  <div
                    className="flex items-center gap-2 mt-2"
                    style={{
                      color:
                        stats.change > 0 ? "#2C4A3B" : stats.change < 0 ? "#B04A32" : "#5C635A",
                    }}
                  >
                    {stats.change > 0 ? (
                      <ArrowUpRight className="w-6 h-6" strokeWidth={1.7} />
                    ) : stats.change < 0 ? (
                      <ArrowDownRight className="w-6 h-6" strokeWidth={1.7} />
                    ) : (
                      <Minus className="w-6 h-6" strokeWidth={1.7} />
                    )}
                    <span className="font-display text-2xl font-medium num">
                      {stats.change >= 0 ? "+" : ""}
                      {formatEuro(stats.change)}
                    </span>
                  </div>
                  {stats.pct !== null && (
                    <p className="text-xs text-[#8B9389] mt-1">
                      {stats.pct >= 0 ? "+" : ""}
                      {stats.pct.toFixed(1)}% from start of period
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-[#E5EFE7] border border-[#829A88]/30 px-4 py-3">
                    <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#5C635A]">Assets</p>
                    <p className="num text-base font-medium text-[#2C4A3B] mt-0.5">
                      {formatEuro(stats.latest.assets_total)}
                    </p>
                  </div>
                  <div className="rounded-xl bg-[#FAEEE5] border border-[#E07A5F]/30 px-4 py-3">
                    <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#5C635A]">Debts</p>
                    <p className="num text-base font-medium text-[#B04A32] mt-0.5">
                      {formatEuro(stats.latest.debts_total)}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="card-soft p-6">
            <p className="label-kicker">Net worth chart</p>
            <h3 className="font-display text-xl text-[#1D3328] mt-1 mb-4">Month-by-month</h3>
            <div className="h-80 min-h-[20rem] w-full" data-testid="networth-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeline} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2C4A3B" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="#2C4A3B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEEBE2" />
                  <XAxis
                    dataKey="month"
                    tickFormatter={formatMonthShort}
                    tick={{ fontSize: 11, fill: "#8B9389" }}
                    interval={Math.max(0, Math.floor(timeline.length / 8))}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#8B9389" }}
                    tickFormatter={(v) => `€${Math.round(v / 1000)}k`}
                  />
                  <Tooltip
                    formatter={(v, name) => [formatEuro(v), name]}
                    labelFormatter={(l) => formatMonthShort(l)}
                    contentStyle={{ background: "#FFF", border: "1px solid #E5E3DB", borderRadius: 12 }}
                  />
                  <ReferenceLine y={0} stroke="#C5CFC3" />
                  <Area
                    type="monotone"
                    dataKey="net_worth"
                    name="Net worth"
                    stroke="#2C4A3B"
                    strokeWidth={2.5}
                    fill="url(#nwGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="card-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="label-kicker">Composition</p>
                <h3 className="font-display text-xl text-[#1D3328] mt-1">
                  Assets vs Debts
                </h3>
              </div>
            </div>
            <div className="h-72 min-h-[18rem] w-full" data-testid="networth-composition">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeline} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEEBE2" />
                  <XAxis
                    dataKey="month"
                    tickFormatter={formatMonthShort}
                    tick={{ fontSize: 11, fill: "#8B9389" }}
                    interval={Math.max(0, Math.floor(timeline.length / 8))}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#8B9389" }}
                    tickFormatter={(v) => `€${Math.round(v / 1000)}k`}
                  />
                  <Tooltip
                    formatter={(v, name) => [formatEuro(v), name]}
                    labelFormatter={(l) => formatMonthShort(l)}
                    contentStyle={{ background: "#FFF", border: "1px solid #E5E3DB", borderRadius: 12 }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="assets_total" name="Assets" stroke="#2C4A3B" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="debts_total" name="Debts" stroke="#E07A5F" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="card-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="label-kicker">Monthly snapshots</p>
                <h3 className="font-display text-xl text-[#1D3328] mt-1">All months</h3>
              </div>
              <span className="text-xs text-[#8B9389]">{timeline.length} rows</span>
            </div>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm" data-testid="networth-table">
                <thead>
                  <tr className="text-left label-kicker border-b border-[#EEEBE2]">
                    <th className="py-3 px-2">Month</th>
                    <th className="py-3 px-2 text-right">Assets</th>
                    <th className="py-3 px-2 text-right">Debts</th>
                    <th className="py-3 px-2 text-right">Net worth</th>
                    <th className="py-3 px-2 text-right">Δ vs prev.</th>
                  </tr>
                </thead>
                <tbody>
                  {[...timeline].reverse().map((row, idx) => {
                    const reversed = [...timeline].reverse();
                    const prev = reversed[idx + 1];
                    const delta = prev ? row.net_worth - prev.net_worth : null;
                    return (
                      <tr key={row.month} className="border-b border-[#EEEBE2] hover:bg-[#FAF8F2]/60" data-testid="networth-row">
                        <td className="py-3 px-2 text-[#1D3328] font-medium">{formatMonthShort(row.month)}</td>
                        <td className="py-3 px-2 text-right num text-[#2C4A3B]">{formatEuro(row.assets_total)}</td>
                        <td className="py-3 px-2 text-right num text-[#E07A5F]">{formatEuro(row.debts_total)}</td>
                        <td className="py-3 px-2 text-right num font-medium" style={{ color: row.net_worth >= 0 ? "#1D3328" : "#B04A32" }}>
                          {formatEuro(row.net_worth)}
                        </td>
                        <td
                          className="py-3 px-2 text-right num"
                          style={{ color: delta == null ? "#8B9389" : delta > 0 ? "#2C4A3B" : delta < 0 ? "#B04A32" : "#5C635A" }}
                        >
                          {delta == null ? "—" : `${delta >= 0 ? "+" : ""}${formatEuro(delta)}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
